import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, IconButton, Typography, Divider } from '@mui/material';
import { Refresh as RefreshIcon, Receipt as ReceiptIcon } from '@mui/icons-material';
import api, { handleCatch } from '../api';
import MobileHeader from '../components/MobileHeader';
import EmptyState from '../components/EmptyState';
import { formatInBusinessTimezone } from '../utils/dateTimeHelper';

const BillsScreen = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/school/school-fee-demand-bill/get-my-bills', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setItems(response.data || []);
    } catch (error) {
      console.error('BillsScreen loadData error:', error);
      handleCatch(error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    await loadData();
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="My Bills"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
        }}
      >
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ReceiptIcon />}
            iconColor="#8E24AA"
            title="No Bills"
            subtitle="You have no pending bills at the moment."
            actionLabel="Refresh"
            onAction={onRefresh}
          />
        ) : (
          <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                }}
              >
                {/* Header row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid #f0f0f0',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(142,36,170,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ReceiptIcon sx={{ fontSize: 20, color: '#8E24AA' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>
                      {item.monthName}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#888' }}>
                      Due: {formatInBusinessTimezone(item.dueDate, 'dd MMM yyyy')}
                    </Typography>
                  </Box>
                  {item.duesAmt > 0 && (
                    <Box
                      sx={{
                        backgroundColor: '#FFEBEE',
                        color: '#C62828',
                        fontSize: 12,
                        fontWeight: 700,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 10,
                      }}
                    >
                      Due ₹{item.duesAmt}
                    </Box>
                  )}
                </Box>

                {/* Amount rows */}
                <Box sx={{ px: 2, py: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                    <Typography sx={{ fontSize: 13, color: '#666' }}>Payable</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                      ₹{item.grossAmt}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                    <Typography sx={{ fontSize: 13, color: '#666' }}>Paid</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#2E7D32' }}>
                      ₹{item.paidAmt}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                    <Typography sx={{ fontSize: 13, color: '#666' }}>Dues</Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: item.duesAmt > 0 ? '#C62828' : '#2E7D32',
                      }}
                    >
                      ₹{item.duesAmt}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BillsScreen;
